import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MaskedLines, Reveal } from "@/components/Reveal";

const FAQS = [
  {
    q: "How does payment work? Is my card charged online?",
    a: "No card is charged online. You place your order here, we confirm stock with our supplier, then email you a manual invoice. Your kit ships to your door, gets picked up, or gets installed by us in Auckland — once the invoice is paid.",
  },
  {
    q: "Do you ship, or can I pick up in Auckland?",
    a: "Both — and we also install. Shipping to your door NZ-wide or Auckland pickup/drop-off is a flat $170. Full installation is $200 flat: drop your bike to us (or have your kit shipped straight to us), we build it, and you pick up the finished ride. Accessories-only orders ship for $10 flat, and accessories added to a combo order ride along free.",
  },
  {
    q: "How fast do they go?",
    a: "The 2000W Combo (52V) reaches 76 km/h, and the 3000W Combo (72V) reaches 94 km/h. Range figures are coming soon — we won't publish numbers until we have real ones.",
  },
  {
    q: "Will a conversion kit fit my bike?",
    a: "Most rigid-frame bikes with steel or alloy dropouts take a hub motor. Disc and V-brake bikes are both fine. The pictured battery measures 408 × 365.5 × 239.9 × 84.9 mm. Send us your bike's make, model and year through the contact form and we'll confirm fit before you pay anything.",
  },
  {
    q: "Which battery should I choose — 20Ah Hard Case or 30Ah Soft Case?",
    a: "The 20Ah Hard Case is included and locks to the frame; the 30Ah Soft Case is +$250 and gives more capacity for longer rides. Exact range depends on your bike, terrain and how hard you lean on the throttle — we publish real range figures once supplier spec sheets land.",
  },
  {
    q: "Sine Wave vs Square Wave controller — what's the difference?",
    a: "Sine Wave delivers power more smoothly and runs quieter. Square Wave is the raw, punchy, proven option. Both are included — it comes down to feel.",
  },
  {
    q: "What's the difference between the 2000W and 3000W combos?",
    a: "Power, voltage and speed — the 2000W Combo runs at 52V and tops out at 76 km/h, the 3000W Combo at 72V and 94 km/h. Everything else comes included in both: controller, display, battery, charger, torque arms.",
  },
  {
    q: "Are torque arms really included?",
    a: "Yes — every combo kit ships with torque arms as standard. High-power hub motors put serious torque through your dropouts; torque arms are non-negotiable for us.",
  },
  {
    q: "What warranty do the kits carry?",
    a: "Final warranty terms are being locked in with our supplier — full details will be published here soon. In the meantime, if a component arrives faulty, contact us and we'll sort it with the supplier.",
  },
  {
    q: "How long does an order take?",
    a: "It depends on supplier stock — we confirm the timeline when you order, before you pay. We'll always give you a straight answer on lead time with the invoice email.",
  },
];

export default function FAQ() {
  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 sm:px-8">
      <section className="py-16 sm:py-20">
        <p className="eyebrow mb-5 flex items-center gap-3">
          <span className="inline-block h-px w-8 bg-volt" />
          FAQ
        </p>
        <h1
          className="font-display font-black tracking-tight text-ink leading-[0.95]"
          style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)" }}
          data-testid="faq-heading"
        >
          <MaskedLines
            lines={[
              <span key="a">STRAIGHT</span>,
              <span key="b">
                <span className="text-volt">ANSWERS.</span>
              </span>,
            ]}
          />
        </h1>
        <Reveal delay={0.35}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600">
            Everything people ask before converting. Something missing? The
            contact form lands directly in our inbox.
          </p>
        </Reveal>
      </section>

      <Reveal>
        <Accordion type="single" collapsible className="rounded-2xl border border-black/10 bg-white px-6" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="last:border-0">
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="py-5 text-left text-base font-bold text-ink hover:text-volt hover:no-underline"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-sm leading-relaxed text-zinc-600">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>

      <Reveal delay={0.1} className="mt-12">
        <div className="flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-black/10 bg-surface p-8">
          <div>
            <h2 className="font-display text-xl font-extrabold text-ink">Still stuck?</h2>
            <p className="mt-1.5 text-sm text-zinc-600">
              Send your bike details and we'll point you at the right kit.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="faq-contact-cta"
            className="rounded-[3px] bg-volt px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
          >
            Contact Us
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
